import React, { useState } from 'react';
import { exportNpsCampaignResults } from '@area-restrita/api-client/endpoints/nps/nps.gen';
import { NpsCampaignDto } from '@area-restrita/api-client/model/npsCampaignDto';
import { DownloadIcon, Loader2Icon } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  npsExportIncludesComments,
  getNpsExportEligibility,
  getNpsExportFilename,
  getErrorMessage,
} from '@/modules/nps/utils';

interface IProps {
  campaign: NpsCampaignDto;
  canExport: boolean;
  canViewComments: boolean;
  userType: 'tenant' | 'company' | null;
}

const getHeaderValue = (
  headers: unknown,
  name: string,
): string | null | undefined => {
  if (!headers || typeof headers !== 'object') return null;

  const maybeHeaders = headers as {
    get?: (headerName: string) => string | null | undefined;
    [key: string]: unknown;
  };

  const value = maybeHeaders.get?.(name) ?? maybeHeaders[name];

  return typeof value === 'string' ? value : null;
};

const attachContentDisposition = (data: unknown, headers?: unknown) => {
  if (!(data instanceof Blob)) return data;

  const contentDisposition = getHeaderValue(headers, 'content-disposition');
  if (!contentDisposition) return data;

  Object.defineProperty(data, 'contentDisposition', {
    value: contentDisposition,
    configurable: true,
  });

  return data;
};

const saveBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const NpsCampaignExportButton: React.FC<IProps> = ({
  campaign,
  canExport,
  canViewComments,
  userType,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const eligibility = getNpsExportEligibility({
    campaign,
    canExport,
    userType,
  });

  if (!eligibility.shouldShow) return null;

  const isPartial = campaign.status === 'open';
  const includesComments = npsExportIncludesComments({
    campaign,
    canViewComments,
    userType,
  });
  const disabled = !eligibility.enabled || isExporting;
  const title = isPartial
    ? 'Baixar exportação parcial'
    : 'Baixar exportação final';
  const commentsHint = includesComments
    ? 'Comentários serão incluídos quando existirem.'
    : 'Comentários não serão incluídos.';
  const tooltipMessage =
    eligibility.disabledReason ??
    `${title}. ${isPartial ? 'Os dados ainda são parciais. ' : ''}${commentsHint}`;

  const handleExport = async () => {
    if (disabled) return;

    try {
      setIsExporting(true);
      const blob = await exportNpsCampaignResults(campaign.id, {
        transformResponse: [attachContentDisposition],
      });
      const filename = getNpsExportFilename(
        campaign,
        (blob as { contentDisposition?: string }).contentDisposition,
      );

      saveBlob(blob, filename);
      toast.success('Exportação NPS baixada com sucesso.');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao gerar exportação NPS.'));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={title}
              disabled={disabled}
              onClick={handleExport}
            >
              {isExporting ? (
                <Loader2Icon
                  data-icon="inline-start"
                  className="animate-spin"
                />
              ) : (
                <DownloadIcon data-icon="inline-start" />
              )}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-64 text-left">
          {tooltipMessage}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default NpsCampaignExportButton;
